import { PatientService } from 'src/services/patient.service'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { getFullName } from 'src/utils/string-formatter.util'
import { usePatientList } from 'src/pages/medical/patients/_components/patients.provider'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

type Props = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  isModalOpen: boolean
  onToggle: (event?: React.MouseEvent<HTMLElement>) => void
}

const DeleteTestPatientModal = ({ patient, isModalOpen, onToggle }: Props) => {
  const { t } = useTranslation()
  const { project, isFeatureActivated } = useCurrentProject()
  const { notify } = useNotification()
  const { updatePatientList } = usePatientList()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const tabIdFromUrl = searchParams.get('tabId')

  const handleDeleteTestPatient = async () => {
    try {
      const result = await PatientService.delete(project.id, patient.id)
      notify({
        title: t('common.actions.success'),
        message: t('pages.patient-page.delete-notification-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })

      localStorage.removeItem('humanReviewSelectedPatientMedEventId')
      queryClient.removeQueries({ queryKey: ['human-review-patient-comments', patient.id] })
      queryClient.removeQueries({ queryKey: ['human-review-requests-detail', project.id] })
      queryClient.removeQueries({ queryKey: ['human-review-requests-tabs', project.id] })
      queryClient.removeQueries({ queryKey: ['human-review-requests-list', project.id] })

      const isOnOldPatientList = !isFeatureActivated('is-planning-patient-activated')
      if (isOnOldPatientList) {
        updatePatientList({ method: 'DELETE', data: { patient: result } })
        navigate(`/${project.id}/medical/patients`)
      } else {
        // reload current planning tab patient med events list
        await queryClient.invalidateQueries({
          queryKey: ['planning-patient-med-events', Number(tabIdFromUrl)],
        })
      }
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('pages.patient-page.delete-notification-error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      onToggle()
    }
  }

  return (
    <CurecallConfirmationModal
      isOpen={isModalOpen}
      color="ALERT"
      onConfirm={handleDeleteTestPatient}
      onCancel={() => onToggle()}
      title={t('pages.patient-page.confirmation-modal-delete-title')}
      cancelButtonLabel={t('common.actions.cancel')}
      confirmButtonLabel={t('common.actions.delete')}
    >
      <div className="c-flex c-flex-col c-gap-4 c-items-start">
        <span>{t('pages.patient-page.confirmation-modal-delete-description')}</span>
        <span className="c-mx-auto c-font-semibold c-text-lg">{getFullName(patient)}</span>
      </div>
    </CurecallConfirmationModal>
  )
}

export default DeleteTestPatientModal
